//
// Forms JS
// --------------------------------------------------
// All the functions specifically for usage with freeform forms including:
// Populating hidden form fields
// Validation and formatting of the phone number field
// Sets error and success message position
// Pushes data to datalayer on form submission for GTM

import Cookies from 'js-cookie';
import { Formatter } from "./helpers/formatter";
import { FacebookConversions } from "./fb-conversions";

window.addEventListener('load', () => {

    // Function that pushes GA data through to datalayer for GTM
    // ...additional datalayer data can be added at time of form submission
    function gtmPush(event, formId, dataLayerSubmission) {
        if (typeof dataLayer !== 'undefined') {
            const intoPlaceEventId = FacebookConversions.generateEventId();

            let dataToPush = {
                event: event,
                formName: formId,
                intoPlaceEventId: intoPlaceEventId
            };

            if (event === 'formSubmitted') {
                dataToPush['userData'] = dataLayerSubmission;
            }

            dataLayer.push(dataToPush);

            return intoPlaceEventId;
        }

        return null;
    }

    // Function that sets field values based on cookies etc.
    function setFormValues(form) {

        // Collect sales rep names from the page
        let salesRepNames = [];
        const salesRepPanels = document.querySelectorAll('.sales-rep-panel');

        salesRepPanels.forEach(panel => {
            const salesRepDivs = panel.querySelectorAll('div[data-sales-rep]');
            salesRepDivs.forEach(div => {
                const salesRepValue = div.getAttribute('data-sales-rep');
                if (salesRepValue && salesRepValue !== 'false' && salesRepValue !== 'null') {
                    salesRepNames.push(salesRepValue);
                }
            });
        });

        // Deduplicate the sales rep names
        const uniqueSalesReps = [...new Set(salesRepNames)];
        const salesRepsString = uniqueSalesReps.join(', ');

        // Array of form fields and the new value that should be assigned to that field
        let formFields = [
            {
                handle: form.utmSource,
                newValue: Cookies.get('utm_source')
            },
            {
                handle: form.utmMedium,
                newValue: Cookies.get('utm_medium')
            },
            {
                handle: form.utmCampaign,
                newValue: Cookies.get('utm_campaign')
            },
            {
                handle: form.utmContent,
                newValue: Cookies.get('utm_content')
            },
            {
                handle: form.state,
                newValue: Cookies.get('location')
            },
            {
                handle: form.fbClickId,
                newValue: Cookies.get('_fbc')
            },
            {
                handle: form.fbBrowserId,
                newValue: Cookies.get('_fbp')
            },
            {
                handle: form.userAgent,
                newValue: navigator.userAgent
            },
            {
                handle: form.gaclientid,
                newValue: Cookies.get('_ga')?.slice(6)
            },
            {
                handle: form.gatrackid,
                newValue: typeof google_tag_manager === 'object' ? Object.keys(google_tag_manager).filter((key) => key.indexOf('G-') === 0) : ''
            },
            {
                handle: form.gclid,
                newValue: Cookies.get('ff_gclid')
            },
            {
                handle: form.pageUrl,
                newValue: window.location
            },
            {
                handle: form.pageTitle,
                newValue: form.dataset.pageTitle
            },
            {
                handle: form.salesReps,
                newValue: salesRepsString
            }
        ]

        // Assign form fields new values if they exist
        for (let field of formFields) {
            if (field.handle && field.newValue) {
                field.handle.value = field.newValue;
            }
        }
    }

    // Adds transition classes to notification, fades to 0 opacity and then hides from dom on transition finish
    function fadeOutNotification(form) {
        let notifications = form.querySelectorAll('.ff-form-errors, .ff-form-success');

        for (let notification of notifications) {
            notification.classList.add('transition-opacity', 'duration-500', 'delay-[15000ms]', 'flex', 'items-center');

            setTimeout(() => {
                notification.classList.add('opacity-0');
            }, 50);

            notification.addEventListener('transitionend', event => {
                notification.classList.add('hidden');
            }, { once: true });
        }
    }

    // Freeform functions
    let forms = document.querySelectorAll('form');

    for (let form of forms) {
        const formId = form.id;

        // Override Freeform default error/success message
        // Commented as should be set on form via cms, but could be useful for a dynamic message on a reusable form
        // freeform.setOption('errorBannerMessage', 'Error!');
        // freeform.setOption('successBannerMessage', 'Success!');

        // Run when freeform initialises
        form.addEventListener("freeform-ready", function (event) {
            // Conditional: show interestedInProject only when interestedIn = 'projects'
            const interestedInSelect = form.querySelector('select[name="interestedIn"]');
            const projectSelect = form.querySelector('select[name="interestedInProject"]');
            const projectFieldContainer = projectSelect?.closest('.freeform-column');

            if (interestedInSelect && projectFieldContainer) {
                const toggleProjectField = () => {
                    const isProjects = interestedInSelect.value === 'projects';
                    projectFieldContainer.style.display = isProjects ? '' : 'none';
                    if (!isProjects && projectSelect) projectSelect.value = '';
                };
                toggleProjectField();
                interestedInSelect.addEventListener('change', toggleProjectField);
            }

            // Phone Validation for: input[data-validate="mobile"]
            const phoneInput = form.querySelector('input[data-validate="mobile"]');
            const phoneValidationMessage = 'Please provide a valid Australian Mobile Phone number.';

            if (phoneInput) {
                phoneInput.addEventListener('change', (event) => {
                    const phoneVal = Formatter.formatMobileNumber(phoneInput.value);

                    if (phoneInput.value !== phoneVal) {
                        phoneInput.value = phoneVal;
                    }

                    if (phoneVal.match(/^\+61 4\d{2} \d{3} \d{3}$/)) {
                        // Clear any errors on the input
                        phoneInput.setCustomValidity('');
                        phoneInput.reportValidity();
                    }

                    // no else statement here as we are matching existing validation flow from freeform where the errors only appear when attempting to submit
                });

                form.addEventListener('freeform-on-submit', function (submitEvent) {
                    if (!phoneInput.value.match(/^\+61 4\d{2} \d{3} \d{3}$/)) {
                        phoneInput.setCustomValidity(phoneValidationMessage);
                        phoneInput.reportValidity();

                        submitEvent.preventDefault();
                    }
                    else {
                        phoneInput.setCustomValidity('');
                        phoneInput.reportValidity();
                    }
                });
            }
        });

        // Runs when freeform has attempted a submission
        form.addEventListener('freeform-on-submit', function (event) {

            // Calls function to set dynamic field values
            setFormValues(form);

            // Sync campaignMonitorEmailId from data attribute (prevents Freeform value syncing issues)
            // Check the form first, then look for a parent wrapper with the attribute
            const formWrapper = form.closest('[data-default-campaign-monitor-email-id]');
            const defaultCampaignMonitorEmailId = form.dataset.defaultCampaignMonitorEmailId || formWrapper?.dataset.defaultCampaignMonitorEmailId;
            if (defaultCampaignMonitorEmailId !== undefined) {
                const campaignMonitorInput = form.querySelector('input[name="campaignMonitorEmailId"]');
                if (campaignMonitorInput) {
                    campaignMonitorInput.value = defaultCampaignMonitorEmailId;
                }
            }
        });

        // Runs after ajax submit
        form.addEventListener('freeform-ajax-after-submit', function(event) {

            // Override error/success notification position on ajax submission
            let notifications = form.querySelectorAll('.ff-form-errors, .ff-form-success');
            let submitButton = form.querySelector('button[data-freeform-action="submit"]');

            for (let notification of notifications) {
                submitButton?.closest('.freeform-row')?.appendChild(notification);
                fadeOutNotification(form);
            }
        });

        // Runs before ajax submit
        form.addEventListener('freeform-ajax-before-submit', function(event) {
            const payload = event.data;
            const eventFields = ['email','emailAddress','phone','telephone']
            form.conversionData = {};

            for (let field of eventFields) {
                if (payload.get(field)) {
                    form.conversionData[field] = payload.get(field);
                }
            }

            const userDataFields = ['firstName', 'lastName', 'email', 'phone', 'interestedIn', 'interestedInProject'];
            form.dataLayerSubmission = {};
            for (let field of userDataFields) {
                const checkedRadio = form.querySelector(`input[type="radio"][name="${field}"]:checked`);
                const select = form.querySelector(`select[name="${field}"]`);
                const input = form.querySelector(`input[name="${field}"]`);
                const value = checkedRadio?.value ?? select?.value ?? input?.value;
                if (value) {
                    form.dataLayerSubmission[field] = value;
                }
            }
        });

        // Runs after successful ajax submission
        form.addEventListener('freeform-ajax-success', function(event) {
            if (formId === 'downloadForm') {
                const formElement = document.getElementById(formId);
                formElement.classList.add('hidden');
                const genericPdfPanel = formElement.closest('.generic-pdf-panel');
                const downloadHidden = genericPdfPanel.querySelector('.download-hidden');
                downloadHidden.classList.remove('hidden');
            } else if (formId.includes('enquiry-download-form') || formId.includes('enquiry-modal-form')) {
                console.log('[Forms] Enquiry form submitted:', formId);

                const enquireDownloadForm = document.getElementById('enquire-download-form');
                const enquireDownloadsAvailable = document.getElementById('enquire-downloads-available');

                // Check if there's a pending download
                const pendingDownloadUrl = form.dataset.pendingDownloadUrl;
                const pendingDownloadTitle = form.dataset.pendingDownloadTitle;

                console.log('[Forms] Pending download URL:', pendingDownloadUrl);
                console.log('[Forms] Pending download title:', pendingDownloadTitle);

                if (pendingDownloadUrl) {
                    console.log('[Forms] Triggering download...');

                    // Trigger the download
                    const downloadLink = document.createElement('a');
                    downloadLink.href = pendingDownloadUrl;
                    downloadLink.download = pendingDownloadTitle || '';
                    downloadLink.target = '_blank';
                    downloadLink.rel = 'noopener nofollow';
                    document.body.appendChild(downloadLink);
                    downloadLink.click();
                    document.body.removeChild(downloadLink);

                    console.log('[Forms] Download triggered');

                    // Clear pending download data
                    delete form.dataset.pendingDownloadUrl;
                    delete form.dataset.pendingDownloadTitle;

                    // Close modal after short delay
                    setTimeout(() => {
                        console.log('[Forms] Closing modal...');
                        const closeButton = document.querySelector('[data-enquire-action="close"]');
                        if (closeButton) {
                            closeButton.click();
                        } else {
                            console.warn('[Forms] Close button not found');
                        }
                    }, 1000);
                } else {
                    console.log('[Forms] No pending download');

                    // No pending download - show downloads available screen (only for download form)
                    if (formId.includes('enquiry-download-form')) {
                        console.log('[Forms] Showing downloads available screen');
                        if (enquireDownloadForm) enquireDownloadForm.dataset.visible = 'false';
                        if (enquireDownloadsAvailable) enquireDownloadsAvailable.dataset.visible = 'true';
                    }
                }

                // Set cookie to allow downloads
                const currentPathString = 'enquiry-download-form-' + window.location.pathname.replace(/\\|\//g,'');
                console.log('[Forms] Setting cookie:', currentPathString);
                Cookies.set(currentPathString, 'complete', { expires: 30 });
            }

            let formContainer = form.closest('div[data-form-complete]');

            if (formContainer) {
                formContainer.dataset.formComplete = 'true';
                setTimeout(() => {
                    formContainer.dataset.formComplete = 'false';
                }, 10000);
            }

            if (formId === 'homeDesignEnquiryModalForm') {
                Cookies.set('homeDesignEnquiry', 'complete', { expires: 30 });
            }

            // Pushes GA data through to datalayer
            const eventId = gtmPush('formSubmitted', formId, form.dataLayerSubmission);

            if (eventId) {
                FacebookConversions.convertGtmPixelEvent('Lead', eventId, form.conversionData)
            }
        });
    }

    // Fix for the broken sprig search when navigating back to a page... why we using a broken search?
    const searchInput = document.querySelector('section#searchPanel input');
    if (searchInput && searchInput.value.length) {
        const event = new KeyboardEvent('keyup', { key: 'Enter', code: 'Enter', which: 13, keyCode: 13, });
        searchInput.dispatchEvent(event);
    }
});